import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { IPageMeta, IVerticalBlockItem, IVideo } from '../../types'

type Props = {
  children: React.ReactNode
  pageMeta: IPageMeta
}

interface IReelsBBContext {
  videos: IVerticalBlockItem[]
  showPlayer: boolean
  handleShowPlayer: (kalturaId: string) => void
  handleClosePlayer: (index: number, kalturaId?: string) => void
}

const defaultState = {
  videos: [],
  showPlayer: false,
  handleShowPlayer: () => {
    return
  },
  handleClosePlayer: () => {
    return
  },
} as IReelsBBContext

export const ReelsBBContext = createContext(defaultState)

const parseOptions = (stringOptions: string) => {
  const options: { [key: string]: string } = {}
  if (!stringOptions) {
    return
  }
  const set = stringOptions.replace(/(\w+=)/g, '\n$1').split('\n')
  set.shift()
  for (let i = 0; i < set.length; i++) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const kv: any = set[i].split('=')
    kv[1] = (kv[1] || '').trim()
    options[kv[0]] = kv[1].replace(/(^['"])|(['"]$)/g, '')
  }
  return options
}

const videoRegex =
  '\\[brightcove_iframe(\\s[\\s\\S]*?)?\\](?:((?!\\s*?(?:\\[brightcove_iframe|\\[/(?!brightcove_iframe)))[\\s\\S]*?)(\\[/brightcove_iframe\\]))?'

const kalturaThumbnailurl =
  'https://cfvod.kaltura.com/p/2748741/sp/274874100/thumbnail/entry_id/'

export const ReelsBBProvider = ({ children, pageMeta }: Props) => {
  const [videos, setVideos] = useState<IVerticalBlockItem[]>([])
  const [showPlayer, setShowPlayer] = useState(false)

  const handleShowPlayer = useCallback(
    (kalturaId: string) => {
      const video = videos.find(v => v.video.kalturaId === kalturaId)
      if (video) {
        setVideos(prev => [
          video,
          ...prev.filter(v => v.video.kalturaId !== kalturaId),
        ])
      }
      setShowPlayer(true)
    },
    [videos]
  )

  const handleClosePlayer = useCallback(
    (index: number, kalturaId?: string) => {
      const video = videos.find(v => v.video.kalturaId === kalturaId)
      if (video) {
        const anchorId = video.anchorId
        if (anchorId) {
          const anchor = document.getElementById(anchorId)
          if (anchor) {
            anchor.scrollIntoView({ behavior: 'smooth' })
          }
        }
      }
      setVideos(prev => prev.sort((a, b) => (a.index || 0) - (b.index || 0)))
      setShowPlayer(false)
    },
    [videos]
  )

  useEffect(() => {
    // Whenever the page's war_comp changes, reset state and rebuild from scratch.
    setVideos([])
    setShowPlayer(false)

    if (!pageMeta.war_comp) {
      return
    }

    const { elements, timelines } = pageMeta.war_comp

    const newVideos: IVerticalBlockItem[] = []

    elements.forEach((element, index) => {
      const videoMatch = element.post_content.match(new RegExp(videoRegex))
      if (!videoMatch) {
        return
      }

      const options = parseOptions(videoMatch[1])
      if (!options) {
        return
      }

      const { video_id, credit, desc, poster, kid, duration = '' } = options
      const videoProps = {
        duration: duration ? parseInt(duration.replace(/'/g, '')) : 0,
        poster: poster,
        kalturaId: kid,
        name: video_id,
        credit: credit,
        description: desc,
        autoPlay: false,
      } as IVideo

      const time = new Date(element.createdOn).toTimeString().slice(0, 5)
      const timeline = timelines ? timelines[String(element.timelineId)] : null
      const link = timeline?.link ?? ''

      newVideos.push({
        video: videoProps,
        link: link,
        link_title: '',
        title: element.post_title,
        poster:
          poster ||
          kalturaThumbnailurl + kid + '/version/100101/width/300/height/550',
        time: time,
        index: index,
        anchorId: 'tl_post_' + element.id,
        ...(timeline?.day != null && { day: timeline.day }),
      })
    })

    setVideos(newVideos)
  }, [pageMeta.war_comp])

  return (
    <ReelsBBContext.Provider
      value={{
        videos,
        showPlayer,
        handleShowPlayer,
        handleClosePlayer,
      }}
    >
      {children}
    </ReelsBBContext.Provider>
  )
}

export const useReelsBB = () => {
  const context = useContext(ReelsBBContext)
  if (context === undefined) {
    throw new Error('useReelsBB must be used within a ReelsBBProvider')
  }
  return context
}
